import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { Stethoscope, ArrowLeft } from "lucide-react";

export default function DoctorAuth() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("login");

  const [loginData, setLoginData] = useState({
    email: "",
    password: "",
  });

  const [registerData, setRegisterData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    name: "",
    specialty: "",
    professional_title: "",
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, loginData);
      localStorage.setItem("doctorToken", response.data.access_token);
      toast.success("Autentificare reușită!");
      navigate("/doctor/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Autentificare eșuată");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();

    if (registerData.password !== registerData.confirmPassword) {
      toast.error("Parolele nu se potrivesc");
      return;
    }

    if (registerData.password.length < 6) {
      toast.error("Parola trebuie să aibă cel puțin 6 caractere");
      return;
    }

    setLoading(true);

    try {
      const { confirmPassword, ...dataToSend } = registerData;
      const response = await axios.post(`${API}/auth/register`, dataToSend);
      localStorage.setItem("doctorToken", response.data.access_token);
      toast.success("Înregistrare reușită!");
      navigate("/doctor/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Înregistrare eșuată");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-blue-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Button
          data-testid="back-home-btn"
          onClick={() => navigate("/")}
          variant="ghost"
          className="mb-4 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Înapoi la Pagina Principală
        </Button>

        <Card className="p-8 bg-white/80 backdrop-blur-md border border-gray-200 shadow-xl">
          <div className="flex items-center justify-center gap-2 mb-6">
            <Stethoscope className="w-8 h-8 text-teal-600" />
            <h1 className="text-2xl font-bold text-gray-800">DocPlan</h1>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger data-testid="login-tab" value="login">Autentificare</TabsTrigger>
              <TabsTrigger data-testid="register-tab" value="register">Înregistrare</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label htmlFor="login-email">Email</Label>
                  <Input
                    data-testid="login-email-input"
                    id="login-email"
                    type="email"
                    placeholder="doctor@exemplu.ro"
                    value={loginData.email}
                    onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="login-password">Parolă</Label>
                  <Input
                    data-testid="login-password-input"
                    id="login-password"
                    type="password"
                    placeholder="••••••••"
                    value={loginData.password}
                    onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                    required
                    className="mt-1"
                  />
                </div>
                <Button
                  data-testid="login-submit-btn"
                  type="submit"
                  disabled={loading}
                  className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-full h-11"
                >
                  {loading ? "Se autentifică..." : "Autentificare"}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="register">
              <form onSubmit={handleRegister} className="space-y-4">
                <div>
                  <Label htmlFor="register-name">Nume Complet</Label>
                  <Input
                    data-testid="register-name-input"
                    id="register-name"
                    type="text"
                    placeholder="Dr. Ion Popescu"
                    value={registerData.name}
                    onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="register-email">Email</Label>
                  <Input
                    data-testid="register-email-input"
                    id="register-email"
                    type="email"
                    placeholder="doctor@exemplu.ro"
                    value={registerData.email}
                    onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="register-specialty">Specialitate</Label>
                  <Input
                    data-testid="register-specialty-input"
                    id="register-specialty"
                    type="text"
                    placeholder="Cardiologie, Pediatrie, etc."
                    value={registerData.specialty}
                    onChange={(e) => setRegisterData({ ...registerData, specialty: e.target.value })}
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="register-title">Grad Profesional</Label>
                  <Input
                    data-testid="register-title-input"
                    id="register-title"
                    type="text"
                    placeholder="Primar, Specialist, etc."
                    value={registerData.professional_title}
                    onChange={(e) => setRegisterData({ ...registerData, professional_title: e.target.value })}
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="register-password">Parolă</Label>
                  <Input
                    data-testid="register-password-input"
                    id="register-password"
                    type="password"
                    placeholder="••••••••"
                    value={registerData.password}
                    onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="register-confirm-password">Confirmă Parola</Label>
                  <Input
                    data-testid="register-confirm-password-input"
                    id="register-confirm-password"
                    type="password"
                    placeholder="••••••••"
                    value={registerData.confirmPassword}
                    onChange={(e) => setRegisterData({ ...registerData, confirmPassword: e.target.value })}
                    required
                    className="mt-1"
                  />
                </div>
                <Button
                  data-testid="register-submit-btn"
                  type="submit"
                  disabled={loading}
                  className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-full h-11"
                >
                  {loading ? "Se creează contul..." : "Creează Cont"}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </Card>
      </div>
    </div>
  );
}
